import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import type { BondDocument, BondSubmission } from "@/lib/brokerBonds";
import { readBondActions } from "@/lib/bondStateModel";
import { statusBadgeClass } from "@/lib/listFormat";
import {
  PROFILE_DOCUMENTS_SHOWN,
  documentUploadTypes,
  requiredDocumentCount,
  submissionDocumentRows,
  type DocumentStatusChip,
  type SubmissionDocumentRow,
} from "@/lib/submissionDocuments";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import { buttonClass } from "@/components/buttonStyles";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import {
  DANGER_BADGE,
  LockedButton,
} from "@/components/bond-submission/SubmissionShell";
import { SubmissionDocumentUploadDialog } from "@/components/bond-submission/SubmissionDocumentUploadDialog";
import { DocumentDownload } from "@/components/surety-account/DocumentDownload";

// BRK-S424, BRK-S502 — the submission's "Submission documents" box: one row
// per document on the order (Document · Category · Related bond · Source ·
// Date · Status), the rows Signal REQUIRED first with an Upload that
// preselects their type, then newest first; Download on a row with a file;
// every row on the tab (BRK-S502 review). "Upload document" in the box's header
// opens the same dialog with no type chosen, and is enabled exactly as the
// order's `actions.docs` says (PMS-S837) — greyed with PMS's reason
// otherwise. On the Submission Profile (`profile`) the box shows the first
// five and "View all N documents in Submission Documents ›".
//
// Status, source and related bond are PMS's (PMS-S843). Nothing
// here moves a row: after an upload's 2xx the page RE-READS the list, and the
// row reads "Received" because the server now says so (NN #7). A table from
// `md:`, a card per row below it (no sideways scroll at 375).

const WRAP = "[overflow-wrap:anywhere]";

function StatusChip({ chip }: { chip: DocumentStatusChip | null }) {
  if (chip === null) return <span className="text-brk-dim">—</span>;
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
        chip.tone === "danger" ? DANGER_BADGE : statusBadgeClass(chip.tone)
      }`}
    >
      {chip.label}
    </span>
  );
}

export function SubmissionDocumentsPanel({
  order,
  documents,
  error,
  onChanged,
  focus = false,
  uploadRequest = 0,
  onViewAll,
}: {
  order: Pick<BondSubmission, "id" | "accountId" | "actions">;
  /** The page's read (`useBondDocuments`): null while loading or failed. */
  documents: BondDocument[] | null;
  error: string | null;
  /** Re-read the list — after an upload's 2xx. */
  onChanged: () => void;
  /** `?focus=documents`: scroll here and focus once settled. */
  focus?: boolean;
  /** Bumped by the action row's "Upload Documents" to open the dialog. */
  uploadRequest?: number;
  /** The Submission Profile's summary: the first five rows and a link that
   *  opens the Submission Documents tab. */
  onViewAll?: () => void;
}) {
  const { authedRequest, authedBlob } = useAuth();
  const {
    types,
    rules,
    labelFor,
    error: typesError,
  } = useDocumentTypes(authedRequest);
  const summary = onViewAll !== undefined;
  const docs = readBondActions(order.actions)?.docs ?? {
    enabled: false,
    reason: null,
  };
  const [dialog, setDialog] = useState<{ type: string } | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);

  // The action row's "Upload Documents" (BRK-S399) lands here and opens the
  // dialog — once per click. From 0, so the click that first opens the tab
  // (and mounts this panel) opens it too.
  const handled = useRef(0);
  useEffect(() => {
    if (uploadRequest === handled.current) return;
    handled.current = uploadRequest;
    if (docs.enabled) setDialog({ type: "" });
  }, [uploadRequest, docs.enabled]);

  const section = useRef<HTMLElement>(null);
  const focused = useRef(false);
  const settled = documents !== null || error !== null;
  useEffect(() => {
    if (!focus || focused.current || !settled) return;
    focused.current = true;
    section.current?.scrollIntoView({ block: "start" });
    section.current?.focus();
  }, [focus, settled]);

  const rows = documents ? submissionDocumentRows(documents) : [];
  // Only the Profile's preview truncates; the full tab shows every row.
  const shown = summary ? rows.slice(0, PROFILE_DOCUMENTS_SHOWN) : rows;
  const required = requiredDocumentCount(documents);

  const openUpload = (type: string) => {
    setUploaded(null);
    setDialog({ type });
  };

  const uploadButton = (label: string, type: string, name: string) =>
    docs.enabled ? (
      <button
        type="button"
        onClick={() => openUpload(type)}
        aria-label={name}
        className={buttonClass({ variant: "primary", size: "sm" })}
      >
        {label}
      </button>
    ) : (
      <LockedButton
        label={label}
        reason={docs.reason}
        className={buttonClass({ variant: "primary", size: "sm" })}
      />
    );

  const rowActions = (r: SubmissionDocumentRow) => (
    <div className="flex flex-wrap items-start gap-2">
      {r.isRequired && uploadButton("Upload", r.type, `Upload ${r.name}`)}
      {r.hasFile && (
        <DocumentDownload
          label={`Download ${r.name}`}
          fallbackName={r.name}
          download={() =>
            downloadAccountDocument(authedBlob, order.accountId, r.id)
          }
        />
      )}
    </div>
  );

  const body = () => {
    if (error !== null) {
      return (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      );
    }
    if (documents === null) {
      return (
        <p role="status" className="text-sm text-brk-dim">
          Loading documents…
        </p>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-brk-border px-4 py-6 text-center text-sm text-brk-dim">
          No documents are on this submission yet.
        </div>
      );
    }
    const base =
      "flex min-w-0 gap-2 md:table-cell md:py-2 md:pr-3 md:align-top";
    // Only a file name, which may carry no space, breaks anywhere: on the
    // short columns `anywhere` let the table shrink them below their longest
    // word, and "Performance bond" broke mid-word at 768 (BRK-S429).
    const nameCell = `${base} ${WRAP}`;
    const cell = `${base} break-words`;
    const label = "w-28 shrink-0 text-brk-dim md:hidden";
    return (
      <>
        {/* A table from `md:`; below it each row is a card with its labels. */}
        <table
          aria-label="Submission documents"
          className="block w-full min-w-0 text-sm md:table"
        >
          <thead className="hidden md:table-header-group">
            <tr className="border-b border-brk-border text-left text-brk-dim">
              {[
                "Document",
                "Category",
                "Related bond",
                "Source",
                "Date",
                "Status",
              ].map((h) => (
                <th key={h} scope="col" className="py-2 pr-3 font-medium">
                  {h}
                </th>
              ))}
              <th scope="col" className="py-2 font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="flex flex-col gap-2 md:table-row-group">
            {shown.map((r) => (
              <tr
                key={r.id}
                className="flex flex-col gap-1 rounded-lg border border-brk-border p-3 md:table-row md:rounded-none md:border-0 md:border-b md:p-0"
              >
                <th
                  scope="row"
                  className={`${nameCell} text-left font-medium text-brk-text`}
                >
                  {r.name}
                </th>
                <td className={cell}>
                  <span className={label}>Category</span>
                  {r.category}
                </td>
                <td className={cell}>
                  <span className={label}>Related bond</span>
                  {r.relatedBond}
                </td>
                <td className={cell}>
                  <span className={label}>Source</span>
                  {r.source}
                </td>
                <td className={`${base} whitespace-nowrap tabular-nums`}>
                  <span className={label}>Date</span>
                  {r.date}
                </td>
                <td className={cell}>
                  <span className={label}>Status</span>
                  <StatusChip chip={r.chip} />
                </td>
                <td className="flex min-w-0 pt-1 md:table-cell md:py-2 md:align-top">
                  {rowActions(r)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {summary && (
          <button
            type="button"
            onClick={onViewAll}
            className="self-start text-sm font-medium text-brk-accent hover:underline"
          >
            View all {rows.length}{" "}
            {rows.length === 1 ? "document" : "documents"} in Submission
            Documents ›
          </button>
        )}
      </>
    );
  };

  return (
    <section
      ref={section}
      tabIndex={-1}
      aria-label="Submission documents"
      className="min-w-0 outline-none"
    >
      <CollapsibleBox
        title="Submission documents"
        actions={uploadButton("Upload document", "", "Upload document")}
      >
        <p className="text-sm text-brk-dim">
          {required > 0
            ? `Signal is waiting on ${String(required)} ${
                required === 1 ? "document" : "documents"
              } — use Upload on the row marked Required.`
            : "Everything filed on this submission, newest first."}
        </p>
        {uploaded && (
          <p role="status" className={`text-sm text-brk-text ${WRAP}`}>
            “{uploaded}” uploaded.
          </p>
        )}
        {body()}
      </CollapsibleBox>

      <SubmissionDocumentUploadDialog
        orderId={order.id}
        open={dialog !== null}
        initialType={dialog?.type ?? ""}
        types={documentUploadTypes(types, documents, labelFor)}
        rules={rules}
        rulesError={typesError}
        onClose={() => setDialog(null)}
        onUploaded={(doc) => {
          setDialog(null);
          setUploaded(doc.filename);
          onChanged();
        }}
      />
    </section>
  );
}
